import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, lastValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import type { Subscription } from 'rxjs';
import { environment } from '../../environments/environment';
import { UploadQueueService } from './upload-queue.service';
import { ProgressService } from './progress.service';
import { UploadFileService } from './upload.service';
import type { UploadRecord } from '../models/upload-record.model';
import { UploadStatus } from '../models/upload-record.model';
import pLimit from 'p-limit';

const RETRY_DELAYS_MS = [5000, 15000, 60000];
const XHR_TIMEOUT_MS = 5 * 60 * 1000;
const CONCURRENCY = 8;

@Injectable({ providedIn: 'root' })
export class UploadEngineService {
  private readonly limit = pLimit(CONCURRENCY);
  private fileMap = new Map<string, File>();

  constructor(
    private http: HttpClient,
    private queue: UploadQueueService,
    private progress: ProgressService,
    private uploadService: UploadFileService
  ) { }

  async upload(files: File[]): Promise<void> {
    const now = Date.now();
    const records: UploadRecord[] = files.map((file) => ({
      fileId: crypto.randomUUID(),
      fileName: file.name,
      fileSize: file.size,
      status: UploadStatus.QUEUED,
      presignedUrl: null,
      s3Key: null,
      retryCount: 0,
      maxRetries: 3,
      nextRetryAt: null,
      errorMessage: null,
      createdAt: now,
      updatedAt: now,
    }));

    const fileMap = new Map<string, File>();
    records.forEach((r, i) => fileMap.set(r.fileId, files[i]));
    this.fileMap = fileMap;

    await this.queue.bulkAdd(records);

    const body = {
      files: records.map((r) => ({
        fileId: r.fileId,
        name: r.fileName,
        size: r.fileSize,
      })),
    };

    const res = await lastValueFrom(
      this.http.post<{
        data: { urls: Array<{ fileId: string; presignedUrl: string; s3Key: string }> };
      }>(`${environment.url}/uploads/init`, body)
    );

    const urls = res?.data?.urls ?? [];
    for (const u of urls) {
      await this.queue.patch(u.fileId, {
        presignedUrl: u.presignedUrl,
        s3Key: u.s3Key,
      });
    }

    this.runPump(fileMap);
  }

  /**
   * Queue add-patient medical record uploads so they appear in the shared progress panel.
   * When the batch is finished (all UPLOADED or FAILED), calls confirm and emits result.
   */
  uploadForPatient(
    userId: string,
    files: File[],
    metadata: Array<{ activity: string; facility: string; recordDate: string }>
  ): Observable<{ succeeded: number; failed: number }> {
    const now = Date.now();
    const records: UploadRecord[] = files.map((file, i) => ({
      fileId: crypto.randomUUID(),
      fileName: file.name,
      fileSize: file.size,
      status: UploadStatus.QUEUED,
      presignedUrl: null,
      s3Key: null,
      retryCount: 0,
      maxRetries: 3,
      nextRetryAt: null,
      errorMessage: null,
      createdAt: now,
      updatedAt: now,
      userId,
      activity: metadata[i]?.activity ?? '',
      facility: metadata[i]?.facility ?? '',
      recordDate: metadata[i]?.recordDate ?? '',
    }));

    const batchFileIds = new Set(records.map((r) => r.fileId));
    records.forEach((r, i) => this.fileMap.set(r.fileId, files[i]));

    const teardown = { sub: null as Subscription | null };
    return new Observable<{ succeeded: number; failed: number }>((subscriber) => {
      (async () => {
        try {
          await this.queue.bulkAdd(records);
          const body = {
            folder: userId,
            files: records.map((r) => ({
              fileId: r.fileId,
              name: r.fileName,
              size: r.fileSize,
            })),
          };
          const res = await lastValueFrom(
            this.http.post<{
              data: { urls: Array<{ fileId: string; presignedUrl: string; s3Key: string }> };
            }>(`${environment.url}/uploads/init`, body, { withCredentials: true })
          );
          const urls = res?.data?.urls ?? [];
          for (const u of urls) {
            await this.queue.patch(u.fileId, {
              presignedUrl: u.presignedUrl,
              s3Key: u.s3Key,
            });
          }
          this.runPump(this.fileMap);
        } catch (err) {
          subscriber.error(err);
          return;
        }

        teardown.sub = this.queue
          .watchAll()
          .pipe(
            filter((all) => {
              const batch = all.filter((r) => batchFileIds.has(r.fileId));
              return (
                batch.length === batchFileIds.size &&
                batch.every(
                  (r) => r.status === UploadStatus.UPLOADED || r.status === UploadStatus.FAILED
                )
              );
            }),
            take(1)
          )
          .subscribe(async (all) => {
            const batch = all.filter((r) => batchFileIds.has(r.fileId));
            const uploaded = batch.filter(
              (r) =>
                r.status === UploadStatus.UPLOADED &&
                r.userId === userId &&
                r.s3Key &&
                r.activity !== null &&
                r.facility !== null &&
                r.recordDate !== null
            );
            const recordsToConfirm = uploaded.map((r) => ({
              s3Key: r.s3Key!,
              activity: r.activity!,
              facility: r.facility!,
              recordDate: r.recordDate!,
            }));
            let succeeded = 0;
            if (recordsToConfirm.length > 0) {
              const confirmRes = await lastValueFrom(
                this.uploadService.confirmPresignedUploads(userId, recordsToConfirm)
              );
              succeeded = confirmRes?.data?.created ?? recordsToConfirm.length;
            }
            const failed = batch.filter((r) => r.status === UploadStatus.FAILED).length;
            subscriber.next({ succeeded, failed: failed + (uploaded.length - succeeded) });
            subscriber.complete();
          });
      })();
      return () => teardown.sub?.unsubscribe();
    });
  }

  /**
   * Queue bulk patient upload (no folder). Files go to pending/{sessionId}/{fileId}/name.
   * Does not call confirm; use for bulk-patient flow where process runs server-side after upload.
   * Emits when all are UPLOADED or FAILED.
   */
  uploadBulkPatientSession(
    sessionId: string,
    files: File[]
  ): Observable<{ succeeded: number; failed: number }> {
    const now = Date.now();
    const records: UploadRecord[] = files.map((file) => ({
      fileId: crypto.randomUUID(),
      fileName: file.name,
      fileSize: file.size,
      status: UploadStatus.QUEUED,
      presignedUrl: null,
      s3Key: null,
      retryCount: 0,
      maxRetries: 3,
      nextRetryAt: null,
      errorMessage: null,
      createdAt: now,
      updatedAt: now,
    }));

    const batchFileIds = new Set(records.map((r) => r.fileId));
    records.forEach((r, i) => this.fileMap.set(r.fileId, files[i]));

    const teardown = { sub: null as Subscription | null };
    return new Observable<{ succeeded: number; failed: number }>((subscriber) => {
      (async () => {
        try {
          await this.queue.bulkAdd(records);
          const res = await lastValueFrom(
            this.http.post<{
              data: {
                urls: Array<{ fileId: string; presignedUrl: string; s3Key: string }>;
                sessionId?: string;
              };
            }>(`${environment.url}/uploads/init`, {
              sessionId,
              files: records.map((r) => ({
                fileId: r.fileId,
                name: r.fileName,
                size: r.fileSize,
              })),
            })
          );
          const urls = res?.data?.urls ?? [];
          for (const u of urls) {
            await this.queue.patch(u.fileId, {
              presignedUrl: u.presignedUrl,
              s3Key: u.s3Key,
            });
          }
          this.runPump(this.fileMap);
        } catch (err) {
          subscriber.error(err);
          return;
        }

        teardown.sub = this.queue
          .watchAll()
          .pipe(
            filter((all) => {
              const batch = all.filter((r) => batchFileIds.has(r.fileId));
              return (
                batch.length === batchFileIds.size &&
                batch.every(
                  (r) =>
                    r.status === UploadStatus.UPLOADED || r.status === UploadStatus.FAILED
                )
              );
            }),
            take(1)
          )
          .subscribe((all) => {
            const batch = all.filter((r) => batchFileIds.has(r.fileId));
            const succeeded = batch.filter((r) => r.status === UploadStatus.UPLOADED).length;
            const failed = batch.filter((r) => r.status === UploadStatus.FAILED).length;
            subscriber.next({ succeeded, failed });
            subscriber.complete();
          });
      })();
      return () => teardown.sub?.unsubscribe();
    });
  }

  async runPump(fileMap: Map<string, File>): Promise<void> {
    const queued = await this.queue.getByStatus(UploadStatus.QUEUED);
    const tasks = queued.map((record) =>
      this.limit(() => {
        const file = fileMap.get(record.fileId);
        return this.uploadOne(record, file ?? null);
      })
    );
    await Promise.all(tasks);
  }

  async uploadOne(record: UploadRecord, file: File | null): Promise<void> {
    if (!file) {
      await this.queue.setStatus(record.fileId, UploadStatus.FAILED, {
        errorMessage: 'File not available',
      });
      return;
    }

    await this.queue.setStatus(record.fileId, UploadStatus.UPLOADING);

    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      const url = record.presignedUrl!;

      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', 'application/pdf');
      xhr.timeout = XHR_TIMEOUT_MS;
      xhr.withCredentials = false;

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && e.total > 0) {
          const pct = (e.loaded / e.total) * 100;
          this.progress.emit(record.fileId, pct);
        }
      });

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          this.queue.setStatus(record.fileId, UploadStatus.UPLOADED);
          this.progress.clear(record.fileId);
        } else {
          const detail = this.getS3ErrorDetail(xhr);
          this.scheduleRetry(record, detail).then(() => resolve());
        }
        resolve();
      };

      xhr.onerror = () => {
        this.scheduleRetry(
          record,
          'Network error (check S3 CORS allows your origin and PUT)'
        ).then(() => resolve());
      };

      xhr.ontimeout = () => {
        this.scheduleRetry(record, 'Timeout').then(() => resolve());
      };

      xhr.send(file);
    });
  }

  /** Extract S3 error code/message from XML or use status text. */
  private getS3ErrorDetail(xhr: XMLHttpRequest): string {
    const status = xhr.status;
    const body = (xhr.responseText || '').trim();
    if (!body) return `HTTP ${status}`;
    const codeMatch = body.match(/<Code>([^<]+)<\/Code>/);
    const msgMatch = body.match(/<Message>([^<]+)<\/Message>/);
    const code = codeMatch ? codeMatch[1] : '';
    const msg = msgMatch ? msgMatch[1] : body.slice(0, 80);
    if (code) return `HTTP ${status}: ${code}${msg ? ` — ${msg}` : ''}`;
    return `HTTP ${status}: ${msg}`;
  }

  async scheduleRetry(record: UploadRecord, errorMessage: string): Promise<void> {
    const retryCount = record.retryCount + 1;
    const maxRetries = record.maxRetries ?? 3;

    if (retryCount >= maxRetries) {
      await this.queue.setStatus(record.fileId, UploadStatus.FAILED, {
        retryCount,
        errorMessage,
      });
      return;
    }

    const delayMs = RETRY_DELAYS_MS[Math.min(retryCount - 1, RETRY_DELAYS_MS.length - 1)];
    const nextRetryAt = Date.now() + delayMs;

    await this.queue.setStatus(record.fileId, UploadStatus.RETRYING, {
      retryCount,
      nextRetryAt,
      errorMessage,
    });

    setTimeout(() => {
      this.queue.setStatus(record.fileId, UploadStatus.QUEUED).then(() => {
        const file = this.fileMap.get(record.fileId) ?? null;
        this.uploadOne(
          { ...record, status: UploadStatus.QUEUED, retryCount, nextRetryAt: null },
          file
        );
      });
    }, delayMs);
  }

  async resumeInterrupted(): Promise<void> {
    const uploading = await this.queue.getByStatus(UploadStatus.UPLOADING);
    for (const r of uploading) {
      await this.queue.setStatus(r.fileId, UploadStatus.QUEUED);
    }

    const now = Date.now();
    const retrying = await this.queue.getByStatus(UploadStatus.RETRYING);
    for (const r of retrying) {
      if (r.nextRetryAt !== null && r.nextRetryAt <= now) {
        await this.queue.setStatus(r.fileId, UploadStatus.QUEUED, {
          nextRetryAt: null,
        });
      }
    }

    const retryingStillWaiting = await this.queue.getByStatus(UploadStatus.RETRYING);
    for (const r of retryingStillWaiting) {
      if (r.nextRetryAt === null) continue;
      const delayMs = Math.max(0, r.nextRetryAt - now);
      setTimeout(() => {
        this.queue.setStatus(r.fileId, UploadStatus.QUEUED, { nextRetryAt: null }).then(() => {
          const file = this.fileMap.get(r.fileId) ?? null;
          this.uploadOne(
            { ...r, status: UploadStatus.QUEUED, nextRetryAt: null },
            file
          );
        });
      }, delayMs);
    }

    const queued = await this.queue.getByStatus(UploadStatus.QUEUED);
    if (queued.length > 0) {
      this.runPump(this.fileMap);
    }
  }

  /** Used by progress panel to retry a failed upload (user must have file in memory only on same session). */
  retryFailed(fileId: string, file: File | null): void {
    this.queue.setStatus(fileId, UploadStatus.QUEUED, {
      retryCount: 0,
      nextRetryAt: null,
      errorMessage: null,
    });
    if (file) this.fileMap.set(fileId, file);
    this.runPump(this.fileMap);
  }
}
